import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import Loading from '@/components/Loading'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { usePlatformOwnerOverviewQuery } from '@/hooks/usePlatformOwnerOverview'
import { ManualPlanCard } from './ui/ManualPlanCard'
import { ManualTopupCard } from './ui/ManualTopupCard'
import { UnsuspendOwnerModal } from '@/pages/platform/ui/UnsuspendOwnerModal'
import { DeleteOwnerModal } from '@/pages/platform/ui/DeleteOwnerModal'
import { isBillingExpired, resolveOwnerStatus } from '@/lib/ownerStatus'
import { DisabledWithTooltip } from '@/components/DisabledWithTooltip'
import { MessagingSubscriptionCard } from './ui/MessagingSubscriptionCard'
import OwnerSubscriptionRequestsCard from './ui/OwnerSubscriptionRequestsCard'

const bytesToHuman = (s: string) => {
  const n = Number(s)
  if (!Number.isFinite(n)) return s
  const kb = 1024
  const mb = kb * 1024
  const gb = mb * 1024
  if (n >= gb) return `${(n / gb).toFixed(2)} GB`
  if (n >= mb) return `${(n / mb).toFixed(2)} MB`
  if (n >= kb) return `${(n / kb).toFixed(2)} KB`
  return `${n} B`
}

export default function PlatformOwnerDetailPage() {
  const { ownerId } = useParams()
  const id = ownerId ?? ''

  const { data, status, error, isFetching } = usePlatformOwnerOverviewQuery(id)

  const owner = data?.owner

  const statusLabel = useMemo(() => {
    if (!owner) return ''
    return resolveOwnerStatus(owner)
  }, [owner])

  const billingExpired = useMemo(() => {
    if (!owner) return false
    return isBillingExpired(owner)
  }, [owner])

  const badgeVariant = useMemo(() => {
    // adapte si ton Badge n’a pas "secondary"
    if (statusLabel === 'ACTIVE') return 'default'
    if (statusLabel === 'EXPIRED') return 'secondary'
    return 'destructive' // SUSPENDED / INACTIVE
  }, [statusLabel])

  if (status === 'pending') return <Loading />
  if (error || !data?.ok || !owner) {
    return <div className='text-sm text-destructive'>Owner introuvable</div>
  }

  const canUnsuspend = owner.suspendedAt && !billingExpired && !owner.deletedAt
  const unsuspendDisabled = !canUnsuspend || isFetching

  const unsuspendReason = (() => {
    if (isFetching) return 'Chargement en cours…'
    if (owner.deletedAt) return 'Owner supprimé.'
    if (!owner.suspendedAt) return 'Owner non suspendu.'
    if (billingExpired)
      return 'Plan expiré : renouvelle / force un plan avant de réactiver l’accès.'
    return undefined
  })()

  return (
    <div className='grid gap-6'>
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <h2 className='text-xl font-semibold'>{owner.name}</h2>
              <p className='text-xs text-muted-foreground'>{owner.id}</p>
            </div>

            <div className='flex items-center gap-2'>
              {owner.deletedAt ? (
                <Badge variant='destructive'>Supprimé</Badge>
              ) : (
                <Badge variant={badgeVariant}>{statusLabel}</Badge>
              )}
              <DeleteOwnerModal
                ownerId={owner.id}
                ownerName={owner.name}
                disabled={!!owner.deletedAt || isFetching}
                onDone={() => {}}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className='grid gap-2 text-sm'>
          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Plan</span>
            <span className='font-medium'>{owner.plan}</span>
          </div>

          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Billing</span>
            <span className='font-medium'>
              {billingExpired ? 'EXPIRED' : 'OK'}
            </span>
          </div>

          <div className='rounded-lg border p-3 space-y-3'>
            <div className='font-medium'>Messages usage</div>

            {(['SMS', 'EMAIL', 'WHATSAPP'] as const).map((ch) => {
              const row = data.messageUsageByChannel?.[ch] ?? {
                planIncluded: 0,
                topupIncluded: 0,
                totalIncluded: 0,
                used: 0,
                remaining: 0,
                overageCfa: 0,
              }
              return (
                <div
                  key={ch}
                  className='grid grid-cols-2 gap-2 text-sm border rounded p-3'
                >
                  <div className='font-medium'>{ch}</div>
                  <div />
                  <div className='text-muted-foreground'>Plan included</div>
                  <div>{row.planIncluded}</div>
                  <div className='text-muted-foreground'>Topup included</div>
                  <div>{row.topupIncluded}</div>
                  <div className='text-muted-foreground'>Total included</div>
                  <div>{row.totalIncluded}</div>
                  <div className='text-muted-foreground'>Used</div>
                  <div>{row.used}</div>
                  <div className='text-muted-foreground'>Remaining</div>
                  <div>{row.remaining}</div>
                  <div className='text-muted-foreground'>Overage CFA</div>
                  <div>{row.overageCfa}</div>
                </div>
              )
            })}
          </div>

          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Subaccounts</span>
            <span className='font-medium'>{data.subaccountsCount}</span>
          </div>

          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Storage used</span>
            <span className='font-medium'>
              {bytesToHuman(data.storageUsage.bytesUsed)}
            </span>
          </div>

          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Plan started</span>
            <span>{owner.planStartedAt ?? '—'}</span>
          </div>

          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Next renewal</span>
            <span>{owner.nextRenewalAt ?? '—'}</span>
          </div>

          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Billing email</span>
            <span>{owner.billingEmail ?? '—'}</span>
          </div>

          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Billing phone</span>
            <span>{owner.billingPhone ?? '—'}</span>
          </div>

          {/* ✅ bouton unsuspend conforme aux règles: bloqué si billing expiré */}
          {owner.suspendedAt ? (
            <div className='mt-4 flex justify-end'>
              <DisabledWithTooltip
                disabled={unsuspendDisabled}
                reason={unsuspendReason}
              >
                <UnsuspendOwnerModal
                  ownerId={owner.id}
                  ownerName={owner.name}
                  disabled={unsuspendDisabled}
                  onDone={() => {}}
                />
              </DisabledWithTooltip>
            </div>
          ) : null}

          {owner.suspensionReason ? (
            <div className='mt-2 rounded-lg border p-3 text-sm'>
              <div className='font-medium'>Suspension reason</div>
              <div className='text-muted-foreground'>
                {owner.suspensionReason}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <OwnerSubscriptionRequestsCard ownerId={owner.id} />

      <div className='grid gap-6 md:grid-cols-2'>
        <ManualPlanCard ownerId={owner.id} currentPlan={owner.plan} />
        <MessagingSubscriptionCard owner={owner} />
        <div className='md:col-span-2'>
          <ManualTopupCard ownerId={owner.id} />
        </div>
      </div>
    </div>
  )
}
